import { useCallback, useRef } from 'react';

/**
 * Restores keyboard focus to the element that opened a state-controlled overlay
 * (dialog/sheet) once it closes.
 *
 * Radix only returns focus automatically when the overlay is opened through its
 * own Trigger. These overlays are opened from React state instead, so Radix has
 * no trigger to restore to and drops focus to <body>. This hook captures whatever
 * had focus when the overlay opened and focuses it again on close.
 *
 * Wire the returned handlers to the Radix Content:
 *   onOpenAutoFocus={captureTrigger} onCloseAutoFocus={onCloseAutoFocus}
 *
 * When the overlay also needs to move focus into itself on open, call
 * `captureTrigger` from a custom onOpenAutoFocus rather than using it directly.
 */
export const useReturnFocusOnClose = () => {
    const triggerRef = useRef<HTMLElement | null>(null);

    // onOpenAutoFocus fires while document.activeElement is still the trigger,
    // before Radix moves focus into the overlay — so it's safe to capture here.
    const captureTrigger = useCallback(() => {
        triggerRef.current = document.activeElement as HTMLElement | null;
    }, []);

    const onCloseAutoFocus = useCallback((event: Event) => {
        event.preventDefault();
        triggerRef.current?.focus();
    }, []);

    return { captureTrigger, onCloseAutoFocus };
};
